import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, Flame, Search } from "lucide-react";
import { toast } from "sonner";

const LEVEL_CLS = {
  Bronze: "level-bronze",
  Silver: "level-silver",
  Gold: "level-gold",
  "Certified Pro": "level-pro",
};

export default function Mechanics() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "+91", workshop_name: "", city: "" });

  const load = () => api.get("/mechanics").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/mechanics", form);
      toast.success("Mechanic onboarded");
      setShowForm(false);
      setForm({ name: "", phone: "+91", workshop_name: "", city: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const filtered = rows.filter((m) =>
    !q || [m.name, m.phone, m.city, m.workshop_name].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="p-8 space-y-6" data-testid="mechanics-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">// roster</div>
          <h1 className="font-display font-black text-3xl tracking-tight mt-1">Mechanics</h1>
          <p className="text-zinc-500 text-sm mt-1">{rows.length} registered on WhatsApp.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-zinc-600" />
            <input
              data-testid="mechanics-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="search name, phone, city..."
              className="bg-[#090A0B] border border-zinc-800 pl-8 pr-3 py-2 text-sm font-mono-data focus:border-amber-500 outline-none w-64"
            />
          </div>
          <button
            data-testid="add-mechanic-btn"
            onClick={() => setShowForm((s) => !s)}
            className="bg-amber-500 hover:bg-amber-600 text-black font-display font-bold uppercase tracking-wider px-4 py-2 text-xs flex items-center gap-2"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} /> Onboard
          </button>
        </div>
      </header>

      {showForm && (
        <form onSubmit={submit} className="gs-card p-5 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="add-mechanic-form">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Full name" className="bg-[#090A0B] border border-zinc-800 px-3 py-2 text-sm font-mono-data outline-none focus:border-amber-500" />
          <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+91xxxxxxxxxx" className="bg-[#090A0B] border border-zinc-800 px-3 py-2 text-sm font-mono-data outline-none focus:border-amber-500" />
          <input value={form.workshop_name} onChange={(e) => setForm({ ...form, workshop_name: e.target.value })}
            placeholder="Workshop name" className="bg-[#090A0B] border border-zinc-800 px-3 py-2 text-sm font-mono-data outline-none focus:border-amber-500" />
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
            placeholder="City" className="bg-[#090A0B] border border-zinc-800 px-3 py-2 text-sm font-mono-data outline-none focus:border-amber-500" />
          <button type="submit" data-testid="submit-mechanic-btn"
            className="bg-amber-500 hover:bg-amber-600 text-black font-display font-bold uppercase tracking-wider px-4 py-2 text-xs">
            Save
          </button>
        </form>
      )}

      <div className="gs-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-900">
            <tr className="text-left overline">
              <th className="px-4 py-2.5 font-display">Mechanic</th>
              <th className="px-4 py-2.5 font-display">Workshop</th>
              <th className="px-4 py-2.5 font-display">City</th>
              <th className="px-4 py-2.5 font-display text-right">Points</th>
              <th className="px-4 py-2.5 font-display text-right">Repairs</th>
              <th className="px-4 py-2.5 font-display text-right">Streak</th>
              <th className="px-4 py-2.5 font-display">Level</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} onClick={() => navigate(`/mechanics/${m.id}`)}
                className="border-b border-zinc-900 hover:bg-zinc-900/40 cursor-pointer"
                data-testid={`mechanic-row-${m.id}`}>
                <td className="px-4 py-3">
                  <div className="text-zinc-100 font-medium">{m.name}</div>
                  <div className="text-xs text-zinc-600 font-mono-data">{m.phone}</div>
                </td>
                <td className="px-4 py-3 text-zinc-400">{m.workshop_name || <span className="text-zinc-700">—</span>}</td>
                <td className="px-4 py-3 text-zinc-400">{m.city || <span className="text-zinc-700">—</span>}</td>
                <td className="px-4 py-3 text-right font-mono-data text-amber-500 font-bold">{m.points}</td>
                <td className="px-4 py-3 text-right font-mono-data text-zinc-300">{m.repairs_completed}</td>
                <td className="px-4 py-3 text-right">
                  <span className="inline-flex items-center gap-1 font-mono-data text-zinc-300">
                    <Flame className="w-3.5 h-3.5 text-amber-500" /> {m.learning_streak}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-1.5 py-0.5 text-[10px] font-display font-bold border ${LEVEL_CLS[m.skill_level] || ""}`}>
                    {m.skill_level.toUpperCase()}
                  </span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-zinc-600 text-sm">No mechanics match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
